import axiosInstance from 'core/network/axiosInstance'
import { SalesReportData } from '../../domain/entities/SalesReport'

interface MonthlySalesRowApi {
  month: string
  month_label: string
  volume_mt: number
  revenue_lakh: number
}

interface AdminKPIsApiResponse {
  reporting_period: string
  ytd_volume_mt: number
  ytd_revenue_lakh: number
  farmers_reached: number
  gross_margin: number
  gross_margin_pct: number
  monthly_sales: MonthlySalesRowApi[]
  total_units: number
  total_sale_amount: number
  cogs: number
}

interface FPOVolumeApi {
  fpo: string
  no_of_units: number
  mt: number
}

interface MoneyRecoveredRowApi {
  fpo: string
  sale_amount: number
  received: number
  pending: number
  recovery_pct: number
}

interface FarmerRegistrationRowApi {
  fpo_name: string
  previous_farmers: number
  today_farmers: number
  total_farmers: number
}

interface FPOSaleRowApi {
  fpo: string
  sale_date: string
  no_of_units: number
  sale_amount: number
  remarks?: string
}

interface AdminFPOBreakdownApiResponse {
  farmer_sales: FPOVolumeApi[]
  money_recovered: MoneyRecoveredRowApi[]
  farmer_registration: FarmerRegistrationRowApi[]
  fpo_wise_sales: FPOSaleRowApi[]
}

interface AdminOperationsApiResponse {
  current_inventory: FPOVolumeApi[]
}

class SalesReportApiDatasource {
  async getSalesReport(): Promise<SalesReportData> {
    const [kpisRes, breakdownRes, operationsRes] = await Promise.all([
      axiosInstance.get<AdminKPIsApiResponse>('/api/v1/dashboard/admin/kpis'),
      axiosInstance.get<AdminFPOBreakdownApiResponse>('/api/v1/dashboard/admin/fpo-breakdown'),
      axiosInstance.get<AdminOperationsApiResponse>('/api/v1/dashboard/admin/operations'),
    ])

    const kpis = kpisRes.data
    const breakdown = breakdownRes.data
    const operations = operationsRes.data

    // Calculate totals for Farmer Sales Table
    const farmerSalesRows = (breakdown.farmer_sales || []).map((row) => ({
      entityName: row.fpo,
      units: row.no_of_units || 0,
      mt: row.mt || 0,
    }))
    const totalFarmerUnits = farmerSalesRows.reduce((sum, r) => sum + r.units, 0)
    const totalFarmerMt = Number(farmerSalesRows.reduce((sum, r) => sum + r.mt, 0).toFixed(4))

    // Calculate totals for Money Recovered Table
    const moneyRecoveredRows = (breakdown.money_recovered || []).map((row) => ({
      entityName: row.fpo,
      totalSaleAmount: row.sale_amount || 0,
      receivedAmount: row.received || 0,
      pendingAmount: row.pending || 0,
    }))
    const totalSaleAmount = moneyRecoveredRows.reduce((sum, r) => sum + r.totalSaleAmount, 0)
    const totalReceivedAmount = moneyRecoveredRows.reduce((sum, r) => sum + r.receivedAmount, 0)
    const totalPendingAmount = moneyRecoveredRows.reduce((sum, r) => sum + r.pendingAmount, 0)

    // Calculate totals for Inventory Status Table
    const inventoryRows = (operations.current_inventory || []).map((row) => ({
      entityName: row.fpo,
      units: row.no_of_units || 0,
      mt: row.mt || 0,
    }))
    const totalInventoryUnits = inventoryRows.reduce((sum, r) => sum + r.units, 0)
    const totalInventoryMt = Number(inventoryRows.reduce((sum, r) => sum + r.mt, 0).toFixed(4))

    // FPO Wise Sales Transactions
    const fpoTransactions = (breakdown.fpo_wise_sales || []).map((t, index) => ({
      id: `TXN-${index + 1}`,
      entityName: t.fpo,
      date: t.sale_date ? new Date(t.sale_date).toISOString().split('T')[0] : '',
      units: t.no_of_units || 0,
      amount: t.sale_amount || 0,
      remark: t.remarks || 'Completed',
    }))
    const fpoTotalUnits = kpis.total_units || fpoTransactions.reduce((sum, r) => sum + r.units, 0)
    const fpoTotalAmount = kpis.total_sale_amount || fpoTransactions.reduce((sum, r) => sum + r.amount, 0)

    return {
      reportTitle: 'Executive Sales & Fulfillment Performance Report',
      periodLabel: kpis.reporting_period || 'Current Season',
      primarySales: {
        monthly: (kpis.monthly_sales || []).map((m) => ({
          month: m.month_label || m.month,
          volumeMt: m.volume_mt || 0,
          revenueLakhs: m.revenue_lakh || 0,
        })),
        ytdVolumeMt: kpis.ytd_volume_mt || 0,
        ytdRevenueLakhs: kpis.ytd_revenue_lakh || 0,
      },
      farmersReached: kpis.farmers_reached || 0,
      farmerSales: [
        ...farmerSalesRows,
        { entityName: 'Total Secondary Sales', units: totalFarmerUnits, mt: totalFarmerMt, isTotal: true },
      ],
      moneyRecovered: [
        ...moneyRecoveredRows,
        {
          entityName: 'Total Recovery Balance',
          totalSaleAmount,
          receivedAmount: totalReceivedAmount,
          pendingAmount: totalPendingAmount,
          isTotal: true,
        },
      ],
      inventoryStatus: [
        ...inventoryRows,
        { entityName: 'Total Warehouse Stock', units: totalInventoryUnits, mt: totalInventoryMt, isTotal: true },
      ],
      fpoWiseSales: {
        transactions: fpoTransactions,
        totalUnits: fpoTotalUnits,
        totalAmount: fpoTotalAmount,
        cogs: kpis.cogs || 0,
      },
      fpoFarmerOnboarding: (breakdown.farmer_registration || []).map((reg) => ({
        fpoName: reg.fpo_name,
        previousFarmers: reg.previous_farmers || 0,
        todayFarmers: reg.today_farmers || 0,
        totalFarmers: reg.total_farmers || 0,
      })),
      presence: [],
    }
  }
}

export const salesReportApiDatasource = new SalesReportApiDatasource()
